/**
 * Outspoken brand logo components — emblem + wordmark.
 * Rendered as inline SVGs for crisp rendering on dark backgrounds.
 */

interface EmblemProps {
  size?: number;
  className?: string;
  color?: string;
}

/** The "O" + radiating bars emblem */
export function OutspokenEmblem({ size = 28, className = "", color = "currentColor" }: EmblemProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      {/* Circle (O) with inner cutout */}
      <circle cx="34" cy="52" r="24" fill={color} />
      <circle cx="34" cy="52" r="9" fill="hsl(var(--background))" />
      {/* Five radiating bars, fanning outward */}
      <rect x="62" y="28" width="6" height="46" rx="2" fill={color} transform="rotate(-12 65 51)" />
      <rect x="70" y="24" width="6" height="50" rx="2" fill={color} transform="rotate(-6 73 49)" />
      <rect x="78" y="22" width="6" height="54" rx="2" fill={color} />
      <rect x="86" y="24" width="6" height="50" rx="2" fill={color} transform="rotate(6 89 49)" />
      <rect x="94" y="28" width="5" height="46" rx="2" fill={color} transform="rotate(12 96.5 51)" />
    </svg>
  );
}

interface WordmarkProps {
  className?: string;
  height?: number;
}

/** The "outspoken" lowercase wordmark — rendered as text for perfect scaling */
export function OutspokenWordmark({ className = "", height = 22 }: WordmarkProps) {
  return (
    <span
      className={`font-serif-display lowercase leading-none ${className}`}
      style={{ fontSize: `${height}px`, letterSpacing: "0.005em", fontWeight: 500 }}
      aria-label="Outspoken"
    >
      outspoken
    </span>
  );
}

interface LogoProps {
  showWordmark?: boolean;
  emblemSize?: number;
  wordmarkHeight?: number;
  className?: string;
}

/** Combined emblem + wordmark lockup */
export default function OutspokenLogo({
  showWordmark = true,
  emblemSize = 28,
  wordmarkHeight = 18,
  className = "",
}: LogoProps) {
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <OutspokenEmblem size={emblemSize} color="currentColor" />
      {showWordmark && <OutspokenWordmark height={wordmarkHeight} />}
    </span>
  );
}
